/**
 * Single source of truth for the club's currency.
 *
 * The club picks one currency in Settings ("default_currency") and it has to read the
 * same way everywhere: the dashboard overview, the stats pages, and every export.
 * Everything below is driven off the table, so adding a currency is a one-line change.
 */

export const DEFAULT_CURRENCY = "MAD";

// `symbol`/`position` drive the on-screen form, `decimals` drives rounding everywhere.
// IQD is quoted in whole dinars (the fils subunit is long out of circulation).
export const CURRENCIES = [
    { code: "MAD", name: "Moroccan Dirham",   flag: "🇲🇦", symbol: "DH",  position: "after",  decimals: 2 },
    { code: "USD", name: "US Dollar",         flag: "🇺🇸", symbol: "$",   position: "before", decimals: 2 },
    { code: "EUR", name: "Euro",              flag: "🇪🇺", symbol: "€",   position: "before", decimals: 2 },
    { code: "GBP", name: "British Pound",     flag: "🇬🇧", symbol: "£",   position: "before", decimals: 2 },
    { code: "AUD", name: "Australian Dollar", flag: "🇦🇺", symbol: "A$",  position: "before", decimals: 2 },
    { code: "TRY", name: "Turkish Lira",      flag: "🇹🇷", symbol: "₺",   position: "before", decimals: 2 },
    { code: "IQD", name: "Iraqi Dinar",       flag: "🇮🇶", symbol: "IQD", position: "after",  decimals: 0 },
];

const BY_CODE = CURRENCIES.reduce((map, entry) => {
    map[entry.code] = entry;
    return map;
}, {});

/** Never throws: an unknown code stored in the database still renders as itself. */
export const getCurrency = (code) => {
    const key = String(code || "").trim().toUpperCase();
    if (BY_CODE[key]) return BY_CODE[key];
    if (!key) return BY_CODE[DEFAULT_CURRENCY];
    return { code: key, name: key, flag: "", symbol: key, position: "after", decimals: 2 };
};

export const getCurrencyDecimals = (code) => getCurrency(code).decimals;
export const getCurrencySymbol = (code) => getCurrency(code).symbol;

/** "🇹🇷 TRY - Turkish Lira", for the Settings dropdown. */
export const getCurrencyLabel = (code) => {
    const currency = getCurrency(code);
    return currency.flag ? `${currency.flag} ${currency.code} - ${currency.name}` : currency.code;
};

/** Price inputs hint at whole units in currencies with no subunit. */
export const getCurrencyPlaceholder = (code) => (getCurrencyDecimals(code) === 0 ? "0" : "0.00");

/** Snap an amount to the currency's smallest unit before it is totalled or exported. */
export const roundMoney = (amount, code) => {
    const factor = 10 ** getCurrencyDecimals(code);
    return Math.round((Number(amount) || 0) * factor) / factor;
};

const groupNumber = (value, decimals) => {
    try {
        return new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(value);
    } catch (_) {
        return value.toFixed(decimals);
    }
};

/** "1,234.50" — grouped, currency-correct decimals, no currency marker. */
export const formatAmount = (amount, code, options = {}) => {
    const decimals = options.decimals ?? getCurrencyDecimals(code);
    return groupNumber(Number(amount) || 0, decimals);
};

/** "1234.50" — ungrouped, so CSV/spreadsheet cells stay parseable as numbers. */
export const formatAmountRaw = (amount, code) => (Number(amount) || 0).toFixed(getCurrencyDecimals(code));

/** "₺1,234.50" — the on-screen form used across the panel. */
export const formatMoney = (amount, code, options = {}) => {
    const currency = getCurrency(code);
    const decimals = options.decimals ?? currency.decimals;
    const number = groupNumber(Number(amount) || 0, decimals);
    return currency.position === "before" ? `${currency.symbol}${number}` : `${number} ${currency.symbol}`;
};

/**
 * "1,234.50 TRY" — ISO code instead of the symbol.
 * jsPDF's built-in fonts are WinAnsi-encoded and cannot draw ₺, so PDF exports use this.
 */
export const formatMoneyCode = (amount, code) => {
    const currency = getCurrency(code);
    return `${formatAmount(amount, currency.code)} ${currency.code}`;
};

/** exceljs cell format, e.g. '#,##0.00 "TRY"'. */
export const getExcelMoneyFormat = (code) => {
    const currency = getCurrency(code);
    const base = currency.decimals === 0 ? "#,##0" : `#,##0.${"0".repeat(currency.decimals)}`;
    return `${base} "${currency.code}"`;
};
